import type { WebsiteBlock } from '../../types'
import { useBranding } from '../../contexts/BrandingContext'
import { useI18n } from '../../i18n'
import { ArrowRight } from 'lucide-react'

interface HeroProps {
  title: string
  titleEn?: string
  subtitle: string
  subtitleEn?: string
  ctaText: string
  ctaTextEn?: string
  ctaUrl: string
  ctaSecondaryText?: string
  ctaSecondaryTextEn?: string
  ctaSecondaryUrl?: string
  backgroundImageUrl?: string
  alignment: 'left' | 'center' | 'right'
  showButlerPrompt?: boolean
}

export default function HeroBlock({ block }: { block: WebsiteBlock }) {
  const { branding } = useBranding()
  const { lang, t } = useI18n()
  const p = block.props as HeroProps

  const align = p.alignment || 'center'
  const textAlign = align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center'
  const itemsAlign = align === 'left' ? 'items-start' : align === 'right' ? 'items-end' : 'items-center'

  const title = (lang === 'en' && p.titleEn ? p.titleEn : p.title) || ''
  const subtitle = (lang === 'en' && p.subtitleEn ? p.subtitleEn : p.subtitle) || ''
  const ctaText = (lang === 'en' && p.ctaTextEn ? p.ctaTextEn : p.ctaText) || ''
  const ctaSecondaryText = (lang === 'en' && p.ctaSecondaryTextEn ? p.ctaSecondaryTextEn : p.ctaSecondaryText) || ''

  // Skip rendering if no content at all
  const hasContent = (title?.trim()) || (subtitle?.trim()) || (ctaText?.trim()) || p.backgroundImageUrl
  if (!hasContent) return null

  return (
    <section
      className="relative min-h-[70vh] flex items-center overflow-hidden"
      style={p.backgroundImageUrl ? { backgroundImage: `url(${p.backgroundImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
    >
      {!p.backgroundImageUrl && (
        <div className="absolute inset-0">
          <div className="absolute -top-[30%] -left-[20%] w-[70%] h-[70%] rounded-full opacity-60"
            style={{ background: `radial-gradient(circle, ${branding.primaryColor}22, transparent 70%)` }} />
          <div className="absolute -bottom-[20%] -right-[15%] w-[60%] h-[60%] rounded-full opacity-50"
            style={{ background: `radial-gradient(circle, ${branding.primaryColor}18, transparent 70%)` }} />
        </div>
      )}

      <div className={`relative z-10 w-full max-w-4xl mx-auto px-6 py-20 flex flex-col ${itemsAlign} ${textAlign}`}>
        {title?.trim() && (
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight"
            style={{ color: branding.secondaryColor }}>
            {title}
          </h1>
        )}
        {subtitle?.trim() && (
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mb-10 leading-relaxed">
            {subtitle}
          </p>
        )}
        <div className={`flex flex-wrap gap-4 ${align === 'center' ? 'justify-center' : ''}`}>
          {ctaText?.trim() && (
            <a
              href={p.ctaUrl || '#'}
              className="inline-flex items-center px-8 py-3.5 rounded-xl text-white text-sm font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: branding.primaryColor }}
            >
              {ctaText}
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          )}
          {ctaSecondaryText?.trim() && (
            <a
              href={p.ctaSecondaryUrl || '#'}
              className="inline-flex items-center px-8 py-3.5 rounded-xl text-sm font-medium border transition-all hover:shadow-sm"
              style={{ color: branding.secondaryColor, borderColor: `${branding.secondaryColor}20` }}
            >
              {ctaSecondaryText}
            </a>
          )}
        </div>
      </div>
    </section>
  )
}
